import { useState } from 'react';
import { config } from './../../Utils/environment.js';
import { getUserLoginToken } from './../../Utils/UserInfo';

/** Absolute Node API URL for a relative path like `api/STP/SaveUploadedFile`.
 * Mirrors useHttp's trailing-slash handling on `config.API_URL`.
 * @param {string} path
 * @returns {string}
 */
export const nodeApiUrl = (path) => {
    const base = (config.API_URL || '').replace(/\/$/, '');
    return `${base}/${String(path).replace(/^\//, '')}`;
};

/** The Authorization value useHttp sends: the login token when the user has one, otherwise the
 * shared API key. The STP application is reachable while logged out, so both cases are live.
 * @returns {string}
 */
export const nodeAuthHeader = () => {
    const token = getUserLoginToken();
    return token ? `Bearer ${token}` : config.API_KEY;
};

/** Read a Node API response body without assuming it is JSON.
 *
 * The two customer-document endpoints keep the legacy .NET contracts -- SaveShortTermDocument answers
 * `{"Message": "..."}` and RemoveShortTermDocument answers the bare text `success`/`error` -- and the
 * layers in front of them answer in yet other shapes: `authenticate` sends plain text on 401 and
 * the global error handler can send an HTML error page. Calling `response.json()` blindly would
 * turn any of those into an unrelated parse error.
 *
 * @param {Response} response
 * @returns {Promise<{text: string, json: object|null}>} `json` is null when the body isn't JSON.
 */
export const readNodeBody = async (response) => {
    const text = await response.text();
    try{
        const json = JSON.parse(text);
        return { text, json: (json && typeof json === 'object') ? json : null };
    }catch{
        return { text, json: null };
    }
};

/** Creates a replacer function to handle circular references in objects during JSON stringification.
 * It uses a `WeakSet` to track seen objects and replaces circular references with a string.
 *
 * @returns {function(string, any): any} A function to be used in JSON.stringify to handle circular references.
 */
const circularReferenceReplacer = () => {
    const seen = new WeakSet();
    return (key, value) => {
        if(typeof value === 'object' && value !== null){
            if(seen.has(value)){
                // Return a placeholder for circular references.
                return '[Circular Reference]';
            }
            seen.add(value);
        }
        // Return the original value if it's not a circular reference.
        return value;
    };
};

/** Custom React hook for sending HTTP requests with loading and error handling.
 *
 * @returns {[boolean, string|null, sendHttpRequest]} - 
 * An array containing:
 *  1. loading state (`boolean`) Indicates if a request is in progress
 *  2. error message (`string|null`) Contains error message if request fails
 *  3. sendHttpRequest Function to make API calls
 */
function useHttp(){
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    /** Sends an HTTP request to the specified URL
     * 
     * @param {string} url - The endpoint URL (will be appended to API_URL)
     * @param {string} method - The HTTP method to use
     * @param {object} body - The request body (will be stringified to JSON)
     * @param {Function} action - Callback function to handle the response data
     * @returns {Promise} The fetch promise
     */
    async function sendHttpRequest(url, method, body, action){
        setLoading(true);
        const endPoint = config.API_URL.endsWith('/') 
            ? `${config.API_URL}${url}`
            : `${config.API_URL}/${url}`;

        console.log(endPoint);
        // console.log(url);

        const headers = {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            // if User is not logged in, use API key authentication
            Authorization: nodeAuthHeader(),
        };

        const PromisedResponse = fetch(endPoint, {
            method: method,
            headers: headers,
            body: body ? JSON.stringify(body, circularReferenceReplacer()) : null,
        }).then(async (response) => {
            setLoading(false);
            if(response.status !== 200){
                throw new Error('Something went wrong. Please try again later');
            }else{
                let data = await response.json();
                setLoading(false);
                // reset response keys from old standards (API uses new but old UI uses old Cap keys)
                if(data.Status){data.status = data.Status;}
                if(data.Data){data.data = data.Data;}
                if(data.Message){data.message = data.Message;}
                action(data);
            }
        }).then((json) => {
            setLoading(false);
        }).catch((error) => {
            setErrorMessage(error);
        });
        return PromisedResponse
    };
    return [loading, errorMessage, sendHttpRequest];
};

export function useHttpDocuments(){
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    async function sendHttpRequest(url, method, body, action){
        setLoading(true);
        // let endPoint = DOCUMENT_URL + url;
        const endPoint = config.DOCUMENT_URL.endsWith('/') 
            ? `${config.DOCUMENT_URL}${url}`
            : `${config.DOCUMENT_URL}/${url}`;
        console.log(endPoint);

        let headers = {
            'Authorization': config.DOCUMENT_API_KEY,
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
        };
        const PromisedResponse = fetch(endPoint, {
            method: method,
            headers: headers,
            body: body === '' ? null : JSON.stringify(body)
        }).then((response) => {
            setLoading(false);
            if(response.status !== 200 || response.status !== 304){
                throw new Error('Something went wrong. Please try again later');
            }else{
                setLoading(false);
                let data = response.json();
                action(data);
            }
        }).then((json) => {
            setLoading(false);
        }).catch((error) => {
            console.log(error);
            setErrorMessage(error);
        });
        return PromisedResponse;
    };
    return [loading, errorMessage, sendHttpRequest];
};

export default useHttp;